import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TOPUP_PACKS } from '@traaker/shared';
import { useCredits } from '../../contexts/CreditContext';
import { creditsService } from '../../services/credits';
import { formatNumber } from '../../i18n/format';

/**
 * Agency credit top-up modal. Controlled by CreditContext (chip click or the
 * INSUFFICIENT_CREDITS interceptor). Buying redirects to a Stripe Checkout;
 * the webhook grants the credits and bounces back to /credits/ledger?topup=success.
 */
export default function TopupModal() {
  const { t } = useTranslation();
  const { topupOpen, closeTopup, recommendedPackKey } = useCredits();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const buy = async (packKey: string) => {
    setError(null);
    setBusyKey(packKey);
    try {
      const { checkoutUrl } = await creditsService.topupCheckout(packKey);
      window.location.href = checkoutUrl;
    } catch (e: any) {
      setError(e?.message || t('topup.errorStart'));
      setBusyKey(null);
    }
  };

  return (
    <Dialog open={topupOpen} onClose={() => setBusyKey(null) ?? closeTopup()} maxWidth="sm" fullWidth>
      <DialogTitle>{t('topup.title')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('topup.help')}
        </Typography>
        <Stack spacing={1.5}>
          {TOPUP_PACKS.map((p) => {
            const recommended = p.key === recommendedPackKey;
            return (
              <Box
                key={p.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  border: '1px solid',
                  borderColor: recommended ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: recommended ? 'action.hover' : 'transparent',
                }}
              >
                <Box>
                  <Typography fontWeight={700}>
                    {t('topup.creditsAmount', { credits: formatNumber(p.credits) })}
                    {recommended && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                        {t('topup.recommended')}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('topup.priceLine', { price: p.priceEur, per1k: (p.priceEur / (p.credits / 1000)).toFixed(0) })}
                  </Typography>
                </Box>
                <Button
                  variant={recommended ? 'contained' : 'outlined'}
                  disabled={!!busyKey}
                  onClick={() => buy(p.key)}
                  startIcon={busyKey === p.key ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {busyKey === p.key ? '…' : t('topup.buy')}
                </Button>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
